import { useState } from "react";
import { motion } from "framer-motion";
import { Reveal, stagger, item } from "./motion";
import { CANCERS } from "@/data";
import { AlertTriangle, Activity, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const RibbonIcon = ({ color, striped }) => (
  <svg width="30" height="42" viewBox="0 0 30 42" fill="none" aria-hidden>
    <defs>
      {striped && (
        <pattern id="st" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width="6" height="6" fill={color} />
          <rect width="3" height="6" fill="#ffffff" />
        </pattern>
      )}
    </defs>
    <path
      d="M15 2 C9 8 4 12 6 20 C7 25 11 27 15 27 C19 27 23 25 24 20 C26 12 21 8 15 2 Z"
      fill="none"
      stroke={striped ? "url(#st)" : color}
      strokeWidth="5"
    />
    <path d="M11 25 L4 40 L10 37 L13 41 Z" fill={striped ? "url(#st)" : color} />
    <path d="M19 25 L26 40 L20 37 L17 41 Z" fill={striped ? "url(#st)" : color} />
  </svg>
);

// varied bento spans
const spans = [
  "md:col-span-7", "md:col-span-5",
  "md:col-span-5", "md:col-span-7",
  "md:col-span-6", "md:col-span-6",
  "md:col-span-12",
];

export const CancerRibbons = ({ onBook }) => {
  const [active, setActive] = useState(null);

  return (
    <section id="cancers" className="py-24 sm:py-32 bg-[#F6F4EE] scroll-mt-24" data-testid="cancers">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="overline text-[#B9735A] mb-5">Areas of Expertise · Awareness</p>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2 className="font-serif-display font-light text-4xl sm:text-6xl text-[#1A1C1A] tracking-tight max-w-2xl">
              Gynecologic cancers we <span className="italic text-[#B9735A]">treat &amp; prevent</span>
            </h2>
            <p className="text-[#4A4D4A] max-w-sm text-sm sm:text-base">
              Each awareness ribbon follows international (ESGO) colour coding. Tap a card to learn the
              risk factors and warning symptoms.
            </p>
          </div>
        </Reveal>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-4 sm:gap-5"
        >
          {CANCERS.map((c, i) => (
            <motion.button
              key={c.key}
              variants={item}
              onClick={() => setActive(c)}
              className={`group text-left rounded-3xl border border-black/5 bg-white p-7 sm:p-8 relative overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow] duration-300 ${spans[i]}`}
              style={{ backgroundColor: "#fff" }}
              data-testid={`cancer-card-${c.key}`}
            >
              <div
                className="absolute inset-x-0 top-0 h-1.5"
                style={{ background: c.ribbon }}
              />
              <div
                className="absolute -right-8 -bottom-8 w-40 h-40 rounded-full opacity-40 group-hover:opacity-70 transition-opacity duration-500"
                style={{ background: c.tint }}
              />
              <div className="relative flex items-start gap-5">
                <RibbonIcon color={c.ribbon} striped={c.striped} />
                <div>
                  <h3 className="font-serif-display text-2xl sm:text-[1.7rem] text-[#1A1C1A] leading-snug">
                    {c.name}
                  </h3>
                  <p className="mt-2 text-sm text-[#4A4D4A] leading-relaxed max-w-md">{c.blurb}</p>
                  <span className="mt-4 inline-block text-xs font-semibold tracking-wide text-[#B9735A]">
                    View risk factors &amp; symptoms →
                  </span>
                </div>
              </div>
            </motion.button>
          ))}
        </motion.div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-2xl bg-[#FDFBF7] border-black/5 p-0 overflow-hidden gap-0" data-testid="cancer-dialog">
          {active && (
            <>
              <div className="px-7 pt-7 pb-5" style={{ background: active.tint }}>
                <DialogHeader className="flex-row items-center gap-4 space-y-0">
                  <RibbonIcon color={active.ribbon} striped={active.striped} />
                  <div>
                    <DialogTitle className="font-serif-display text-3xl text-[#1A1C1A] text-left">
                      {active.name}
                    </DialogTitle>
                    <p className="text-sm text-[#4A4D4A] mt-1 text-left">{active.blurb}</p>
                  </div>
                </DialogHeader>
              </div>
              <div className="grid sm:grid-cols-2 gap-6 p-7">
                <div>
                  <div className="flex items-center gap-2 text-[#B9735A] mb-3">
                    <AlertTriangle size={17} />
                    <p className="overline">Risk Factors</p>
                  </div>
                  <ul className="space-y-2.5">
                    {active.risks.map((r) => (
                      <li key={r} className="text-sm text-[#4A4D4A] flex gap-2.5">
                        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#B9735A] shrink-0" />
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="flex items-center gap-2 text-[#6B7A6A] mb-3">
                    <Activity size={17} />
                    <p className="overline">Warning Symptoms</p>
                  </div>
                  <ul className="space-y-2.5">
                    {active.symptoms.map((s) => (
                      <li key={s} className="text-sm text-[#4A4D4A] flex gap-2.5">
                        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#6B7A6A] shrink-0" />
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="px-7 pb-7 flex items-center justify-between gap-4 flex-wrap">
                <p className="text-xs text-[#4A4D4A] max-w-sm">
                  Early detection saves lives. If you notice any of these symptoms, seek an expert opinion.
                </p>
                <button
                  onClick={() => { setActive(null); onBook(); }}
                  className="rounded-full bg-[#B9735A] text-[#FDFBF7] text-sm font-medium px-6 py-3 hover:-translate-y-0.5 transition-transform duration-300"
                  data-testid="cancer-dialog-book"
                >
                  Book a Consultation
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
